import { SizeType } from '@payflip/types/product-builder';
import {
  ChoiceFieldWrapper,
  GeneralWrapper,
  Grid,
  Heading,
  IconName,
  LinkButton,
  OrderStatusTag,
  OutletBody,
  OutletHeader,
  Paragraph,
  TransactionTile
} from '@payflip/ui';
import { Box, LoadingView } from '@payflip/ui/v2';
import { useContext, useEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router';
import { ChoiceField } from '../../components/choice-field';
import { LocaleContext } from '../../components/locale-provider';
import { ProductFieldType } from '../../generated/types';
import { useDownloadBlob } from '../../hooks/use-download-blob';
import { Spacing } from '../../screens/your-budgets/styled';
import { getChoiceActionFields } from '../../utils/get-choice-action-fields';
import { getChoiceNonActionFields } from '../../utils/get-choice-non-action-fields';
import { getChoiceState } from '../../utils/get-choice-state';
import { cmsChoiceStatusMap } from '../../utils/map-cms-choice-status';
import { message } from '../../utils/message';
import { useGetChoiceDetailsScreenDataQuery } from './queries.cms.generated';
import {
  useGetChoiceByIdLazyQuery,
  useGetPaymentOrdersLazyQuery
} from './queries.generated';

export function ChoiceDetails(): JSX.Element {
  const outletContext = useOutletContext<{
    goBackTo: string;
    budgetId?: string;
    choiceId?: string;
  }>();

  const params = useParams<{
    budgetId: string;
    choiceId: string;
  }>();

  const { locale } = useContext(LocaleContext);

  const choiceId = params.choiceId ?? outletContext.choiceId ?? null;
  const budgetId = params.budgetId ?? outletContext.budgetId ?? null;

  const { data: cmsQueryData } = useGetChoiceDetailsScreenDataQuery({
    locale
  });

  const cmsScreenData =
    cmsQueryData?.adminChoiceDetailsScreen?.data?.attributes ?? null;

  const cmsChoiceStatusTitles =
    cmsQueryData?.choiceStatus?.data?.attributes ?? null;

  const cmsCommonData = cmsQueryData?.common?.data?.attributes ?? null;

  const [
    getChoiceByIdLazyQuery,
    { data: choiceData, previousData: previousChoiceData }
  ] = useGetChoiceByIdLazyQuery({
    fetchPolicy: 'cache-and-network'
  });

  const choice = choiceData?.choice ?? previousChoiceData?.choice ?? null;

  useEffect(() => {
    if (choiceId === null) {
      return;
    }

    getChoiceByIdLazyQuery({
      variables: { id: choiceId }
    });
  }, [choiceId, getChoiceByIdLazyQuery]);

  const [
    getPaymentOrdersLazyQuery,
    { data: paymentOrdersData, previousData: previousPaymentOrdersData }
  ] = useGetPaymentOrdersLazyQuery({
    fetchPolicy: 'cache-and-network'
  });

  const paymentOrders =
    paymentOrdersData?.paymentOrders ??
    previousPaymentOrdersData?.paymentOrders ??
    null;

  useEffect(() => {
    if (!budgetId || !choiceId || !choice || !choice.employee.id) {
      return;
    }

    getPaymentOrdersLazyQuery({
      variables: { choiceId, budgetId, employeeId: choice.employee.id }
    });
  }, [budgetId, choiceId, choice, getPaymentOrdersLazyQuery]);

  const pastPaymentOrders = useMemo(() => {
    return paymentOrders?.filter((order) => order.fulfilled === true);
  }, [paymentOrders]);

  const plannedPaymentOrders = useMemo(() => {
    return paymentOrders?.filter((order) => order.fulfilled === false);
  }, [paymentOrders]);

  const download = useDownloadBlob();

  if (choice === null || !paymentOrders) {
    return <LoadingView />;
  }

  const choiceState = getChoiceState({
    formInputs: choice.formInputs,
    config: choice.config,
    status: choice.status,
    employee: choice.employee,
    product: choice.product,
    id: choice.id,
    approvedAt: choice.approvedAt,
    submittedAt: choice.submittedAt,
    createdAt: choice.createdAt
  });
  const actionFields = getChoiceActionFields(choice.product.fields);
  const nonActionFields = getChoiceNonActionFields(choice.product.fields);
  const choiceFieldCommonProps = { ...choiceState, download };

  return (
    <GeneralWrapper>
      <Box flex="1" overflowY="auto" boxSizing="border-box" width="100%">
        <OutletHeader backgroundColor={choice.product.color}>
          <LinkButton
            internal
            to={outletContext.goBackTo}
            iconPosition="left"
            icon={IconName.ArrowBack}
            variant="tertiary"
            size={SizeType.Small}
          >
            {message(cmsCommonData?.backLinkText, 'Go back')}
          </LinkButton>
          <img src={choice.product.image} alt="" />
          <Heading variant="h3">{choice.product.name}</Heading>
          <Paragraph>
            {choice.employee.user.firstname} {choice.employee.user.lastname}
          </Paragraph>
        </OutletHeader>
        <OutletBody>
          <Heading variant="h4">
            {message(cmsScreenData?.statusTitle, 'Status')}
          </Heading>
          <OrderStatusTag orderStatus={choice.status}>
            {message(
              typeof cmsChoiceStatusTitles !== 'undefined' &&
                cmsChoiceStatusTitles !== null
                ? cmsChoiceStatusTitles[cmsChoiceStatusMap(choice.status)]
                : null,
              choice.status[0] + choice.status.toLowerCase().substring(1)
            )}
          </OrderStatusTag>
          <Heading variant="h4">
            {message(cmsScreenData?.overviewTitle, 'Overview')}
          </Heading>
          <ChoiceFieldWrapper>
            {actionFields.length > 0 && (
              <Grid>
                {actionFields.map((field) => (
                  <div key={field.label + field.path}>
                    <ChoiceField {...field} {...choiceFieldCommonProps} />
                  </div>
                ))}
              </Grid>
            )}
            {nonActionFields.length > 0 && (
              <Grid>
                {nonActionFields.map((field, index) => (
                  <div
                    key={field.label + field.path + index}
                    style={{
                      gridColumn:
                        field.type === ProductFieldType.LongText
                          ? 'span 2'
                          : 'unset'
                    }}
                  >
                    <ChoiceField {...field} {...choiceFieldCommonProps} />
                  </div>
                ))}
              </Grid>
            )}
          </ChoiceFieldWrapper>
          <Spacing>
            {pastPaymentOrders && pastPaymentOrders.length > 0 && (
              <>
                <Spacing>
                  <Heading variant="h4">
                    {message(
                      cmsScreenData?.pastTransactionsText,
                      'Past transactions'
                    )}
                  </Heading>
                </Spacing>
                {pastPaymentOrders.map((order) => (
                  <TransactionTile
                    key={order.id}
                    amount={order.expectedAmount}
                    title={order.budget.name[locale] || order.budget.name.en}
                    date={order.dueDate}
                  />
                ))}
              </>
            )}
          </Spacing>
          <Spacing>
            {plannedPaymentOrders && plannedPaymentOrders.length > 0 && (
              <>
                <Spacing>
                  <Heading variant="h4">
                    {message(
                      cmsScreenData?.reservedTransactionsText,
                      'Future transactions'
                    )}
                  </Heading>
                </Spacing>
                {plannedPaymentOrders.map((order) => (
                  <TransactionTile
                    key={order.id}
                    amount={order.expectedAmount}
                    title={order.budget.name[locale] || order.budget.name.en}
                    date={order.dueDate}
                  />
                ))}
              </>
            )}
          </Spacing>
        </OutletBody>
      </Box>
    </GeneralWrapper>
  );
}
